import { useCallback, useEffect, useState } from 'react'

const noOp = () => void 0

export interface StorageAdapter {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

type NavigationPreferences = {
  expandedSections: string[]
}

const getStoredPreferences = (
  storageKey: string,
  storage: StorageAdapter
): NavigationPreferences => {
  try {
    const stored = storage.getItem(storageKey)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (Array.isArray(parsed.expandedSections)) {
        return parsed
      }
    }
  } catch (_error) {
    noOp()
  }
  return { expandedSections: [] }
}

const savePreferences = (
  storageKey: string,
  preferences: NavigationPreferences,
  storage: StorageAdapter
): void => {
  try {
    storage.setItem(storageKey, JSON.stringify(preferences))
  } catch (_error) {
    noOp()
  }
}

export interface UseNavigationPreferencesOptions {
  storageKey?: string
  storage?: StorageAdapter
}

export function useNavigationPreferences(
  options?: UseNavigationPreferencesOptions
) {
  const storageKey = options?.storageKey
  const storage = options?.storage

  const [expandedSections, setExpandedSections] = useState<string[]>(() =>
    storageKey && storage
      ? getStoredPreferences(storageKey, storage).expandedSections
      : []
  )

  const toggleSection = useCallback(
    (sectionId: string) => {
      setExpandedSections((prev) => {
        const isExpanded = prev.includes(sectionId)
        const updated = isExpanded
          ? prev.filter((id) => id !== sectionId)
          : [...prev, sectionId]

        if (storageKey && storage) {
          savePreferences(storageKey, { expandedSections: updated }, storage)
        }
        return updated
      })
    },
    [storageKey, storage]
  )

  const isSectionExpanded = useCallback(
    (sectionId: string) => expandedSections.includes(sectionId),
    [expandedSections]
  )

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue)
          if (Array.isArray(parsed.expandedSections)) {
            setExpandedSections(parsed.expandedSections)
          }
        } catch {
          noOp()
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [storageKey])

  return {
    expandedSections,
    toggleSection,
    isSectionExpanded,
  }
}
